/**
 * Applications — what a part fits (Master Specification section 13, F031).
 *
 * The whole of section 13 is five lines: a part may have compatibility, "Placa
 * ABC → Brastemp modelo X, Consul modelo Y", and it must be relational and
 * searchable. What it does not say is whether compatibility is a TREE (brand →
 * model) or a flat list of labels.
 *
 * A tree, and the reason is the reason a register exists at all. Flat labels
 * return to the problem this replaces: "A20", "Galaxy A20" and "SAMSUNG A20"
 * become three things, and a search for one finds neither of the others — which
 * is exactly the position of a shop that writes the model into the product's
 * name today.
 *
 * ## The slug is the whole of it
 *
 * Uniqueness runs off a normalised form rather than off what was typed, because
 * the failure this register prevents is people typing the same thing three
 * ways. `Galaxy A20`, `galaxy  a20` and `GALAXY A20` are one model; the display
 * name keeps whatever the shop wrote, and the slug decides identity.
 *
 * Accents are folded for the same reason and one more: on a counter keyboard,
 * under a queue, nobody types `Citroën`. A register that distinguishes it from
 * `Citroen` is a register with two Citroëns in it by Friday.
 */
/** Trimmed, collapsed, unaccented, lower case. Identity, not display. */
export const normalizeApplicationName = (value) => value
    .normalize('NFD')
    // Combining marks, which is what NFD split the accents into.
    .replace(/[̀-ͯ]/g, '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();
export const MAX_APPLICATION_NAME_LENGTH = 80;
/**
 * A name has to survive normalisation.
 *
 * `"   "` and `"---"` differ in that the second is at least something somebody
 * meant; both are refused, because a model whose identity is empty collides
 * with every other empty one and the register silently becomes a single row.
 */
export const isValidApplicationName = (value) => {
    const slug = normalizeApplicationName(value);
    return slug.length > 0 && slug.length <= MAX_APPLICATION_NAME_LENGTH;
};
export const APPLICATION_STATUSES = ['active', 'archived'];
/**
 * How a model reads in one line, which is the form the counter needs.
 *
 * "Galaxy A20" alone is ambiguous across brands and useless in a list of
 * matches; the brand is not decoration here, it is half the identity.
 */
export const applicationLabel = (model) => {
    const base = `${model.brandName} ${model.name}`;
    const reference = model.reference === null || model.reference === undefined
        ? ''
        : ` (${model.reference})`;
    return `${base}${reference}`;
};
//# sourceMappingURL=applications.js.map